import { Component } from '@theme/component';
import { getShadeFilter, setShadeFilter } from '@theme/shade-filter-preference';

export default class VariantFilter extends Component {
  /** @type {string} */
  #activeFilter = 'all';

  connectedCallback() {
    super.connectedCallback();

    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener('variant:update', (event) => {
        queueMicrotask(() => {
          this.#resetFilterIfNeeded(event);
          this.#applyFilter();
        });
      });
    }

    this.#restorePreferredFilter();
    this.#initDragScroll();
  }

  /**
   * Re-applies the filter the shopper picked earlier so the list does not reset to every
   * shade on each page load. Silently does nothing when this product has no such filter.
   *
   * The remembered filter deliberately wins over the variant in the URL: landing on a
   * shade the filter hides will switch the selection to the first shade that matches.
   * That means a link pointing at one specific shade is overridden for a shopper who has
   * a filter remembered. Decided deliberately — the filter is meant to lead.
   */
  #restorePreferredFilter() {
    const preferred = getShadeFilter();
    if (!preferred) return;

    const buttons = Array.from(this.querySelectorAll('.variant-filter__button'));
    const match = buttons.find((btn) => btn.dataset.filterValue === preferred);
    if (!match) return;

    for (const btn of buttons) {
      const isMatch = btn === match;
      btn.classList.toggle('variant-filter__button--active', isMatch);
      btn.setAttribute('aria-pressed', String(isMatch));
    }
    this.#activeFilter = preferred;

    const apply = () => {
      VariantFilter.#applyAllFilters(this.closest('.shopify-section'));
      this.#autoSelectIfNeeded();
    };

    // This element can upgrade before the variant picker it filters has been parsed, in
    // which case applying now would find nothing to filter.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', apply, { once: true });
    } else {
      queueMicrotask(apply);
    }
  }

  /**
   * Returns the parsed JSON data from the embedded script tag.
   * @returns {{ variantFilters: Record<string, string|null>, optionValueVariants: Record<string, string[]> }}
   */
  get filterData() {
    if (!this._filterData) {
      const script = this.querySelector('script[data-variant-filter-map]');
      this._filterData = script ? JSON.parse(script.textContent) : { variantFilters: {}, optionValueVariants: {} };
    }
    return this._filterData;
  }

  /**
   * Handles a filter button click via on:click delegation.
   * @param {Event} event
   */
  handleFilterClick(event) {
    const button = event.target.closest('.variant-filter__button');
    if (!button) return;

    const filterValue = button.dataset.filterValue;

    // Update active button styling
    for (const btn of this.querySelectorAll('.variant-filter__button')) {
      btn.classList.remove('variant-filter__button--active');
      btn.setAttribute('aria-pressed', 'false');
    }
    button.classList.add('variant-filter__button--active');
    button.setAttribute('aria-pressed', 'true');

    this.#activeFilter = filterValue;
    setShadeFilter(filterValue);
    VariantFilter.#applyAllFilters(this.closest('.shopify-section'));
    this.#autoSelectIfNeeded();
  }

  /**
   * Checks whether a variant passes this filter instance.
   * @param {string} variantId
   * @returns {boolean}
   */
  passesFilter(variantId) {
    if (this.#activeFilter === 'all') return true;
    const filterName = this.filterData.variantFilters[variantId];
    return filterName === this.#activeFilter;
  }

  /**
   * Applies all variant-filter instances within a section (AND logic).
   * A variant must pass every active filter to remain visible.
   * @param {Element|null} section
   */
  static #applyAllFilters(section) {
    if (!section) return;

    const filters = /** @type {VariantFilter[]} */ (Array.from(section.querySelectorAll('variant-filter')));
    if (!filters.length) return;

    VariantFilter.#applyToDropdown(section, filters);

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    // Use the first filter's optionValueVariants map (all share the same product)
    const { optionValueVariants } = filters[0].filterData;

    const elements = variantPicker.querySelectorAll('[data-option-value-id]');
    for (const element of elements) {
      const optionValueId = element.dataset.optionValueId;
      const variantIds = optionValueVariants[optionValueId] || [];

      const isOption = element.tagName === 'OPTION';
      const label = isOption ? null : element.closest('label');
      const target = isOption ? element : label;

      if (!target) continue;

      // Show if ANY variant for this option value passes ALL active filters
      const passes = variantIds.some((id) => filters.every((f) => f.passesFilter(id)));

      if (isOption) {
        target.hidden = !passes;
      } else if (passes) {
        target.classList.remove('variant-filter--hidden');
      } else {
        target.classList.add('variant-filter--hidden');
      }
    }
  }

  /**
   * Hides the variant dropdown's rows when their variant does not pass every active filter,
   * so the list stops offering shades the shopper has filtered out.
   *
   * The dropdown is filtered separately from the swatch picker because its rows carry the
   * variant id directly (data-variant-id), while the picker's elements carry an option
   * value id that has to be mapped to variants first.
   * @param {Element} section
   * @param {VariantFilter[]} filters
   */
  static #applyToDropdown(section, filters) {
    for (const option of section.querySelectorAll('variant-dropdown .variant-dropdown__option')) {
      const { variantId } = /** @type {HTMLElement} */ (option).dataset;
      if (!variantId) continue;

      const passes = filters.every((filter) => filter.passesFilter(variantId));
      option.classList.toggle('variant-filter--hidden', !passes);
    }
  }

  /**
   * Resets the filter to "All" if the newly selected variant doesn't pass the active filter.
   * @param {CustomEvent} event
   */
  #resetFilterIfNeeded(event) {
    if (this.#activeFilter === 'all') return;

    const variantId = String(event.detail?.resource?.id ?? '');
    if (!variantId || this.passesFilter(variantId)) return;

    // Reset to "All", and forget the stored choice so it matches what is on screen.
    this.#activeFilter = 'all';
    setShadeFilter(null);
    for (const btn of this.querySelectorAll('.variant-filter__button')) {
      const isAll = btn.dataset.filterValue === 'all';
      btn.classList.toggle('variant-filter__button--active', isAll);
      btn.setAttribute('aria-pressed', String(isAll));
    }
  }

  /**
   * Instance wrapper so the variant:update listener can re-apply.
   */
  #applyFilter() {
    VariantFilter.#applyAllFilters(this.closest('.shopify-section'));
  }

  /**
   * Auto-selects the first visible variant when the current selection is filtered out.
   */
  #autoSelectIfNeeded() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    // Check radio inputs
    const checkedInput = variantPicker.querySelector('fieldset input:checked');
    if (checkedInput) {
      const label = checkedInput.closest('label');
      if (label && label.classList.contains('variant-filter--hidden')) {
        const firstVisible = variantPicker.querySelector(
          'fieldset label:not(.variant-filter--hidden) input[type="radio"]'
        );
        if (firstVisible) {
          firstVisible.click();
        }
      }
      return;
    }

    // Check select dropdowns
    const select = variantPicker.querySelector('select');
    if (select) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && selectedOption.hidden) {
        for (const option of select.options) {
          if (!option.hidden) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    }
  }

  // ── Drag-to-scroll on the button row ────────────────────────────────────

  #initDragScroll() {
    const scroller = this.querySelector('[ref="scroller"]');
    if (!scroller) return;

    let isDown = false;
    let startX = 0;
    let scrollStart = 0;
    let hasDragged = false;

    scroller.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return; // native touch scrolling handles this
      if (e.button !== 0) return;
      isDown = true;
      hasDragged = false;
      startX = e.clientX;
      scrollStart = scroller.scrollLeft;
      scroller.style.cursor = 'grabbing';
    });

    scroller.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) hasDragged = true;
      scroller.scrollLeft = scrollStart - dx;
    });

    const stopDrag = () => {
      if (!isDown) return;
      isDown = false;
      scroller.style.cursor = '';
    };

    scroller.addEventListener('pointerup', stopDrag);
    scroller.addEventListener('pointercancel', stopDrag);

    // Suppress button clicks that were actually drags
    scroller.addEventListener('click', (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      }
    }, true);
  }

  // ── Scrollbar arrow handlers (delegated from slideshow-scrollbar arrows) ──

  /**
   * Scroll the filter buttons row to the left.
   */
  previous() {
    const scroller = this.querySelector('[ref="scroller"]');
    if (!scroller) return;
    scroller.scrollBy({ left: -scroller.clientWidth * 0.75, behavior: 'smooth' });
  }

  /**
   * Scroll the filter buttons row to the right.
   */
  next() {
    const scroller = this.querySelector('[ref="scroller"]');
    if (!scroller) return;
    scroller.scrollBy({ left: scroller.clientWidth * 0.75, behavior: 'smooth' });
  }
}

if (!customElements.get('variant-filter')) {
  customElements.define('variant-filter', VariantFilter);
}
